import type React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import PromoBanner from "@/components/home/PromoBanner";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/ui/reveal";
import {
  Glasses, Box, Wrench, Eye, ArrowRight, Star,
  Truck, BadgePercent, Shield, HeartHandshake,
  Heart, ChevronRight, ChevronLeft,
  Instagram,
} from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useHomeSections } from "@/hooks/useHomeSections";
import { useGoogleReviews } from "@/hooks/useGoogleReviews";
import { useFavorites } from "@/contexts/FavoritesContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import lentesHomeIcon from "@/assets/lentes-home-icon.webp";
import acessoriosHomeIcon from "@/assets/acessorios-home-icon.webp";
import pedidolabHomeIcon from "@/assets/pedidolab-home-icon.webp";
import equipamentosHomeIcon from "@/assets/equipamentos-home-icon.webp";
import oculosHomeIcon from "@/assets/oculos-home-icon.webp";
import bannerOculosCompleto from "@/assets/banner-oculos-completo.webp";
import bannerOculosSolar from "@/assets/banner-oculos-solar.webp";
import bannerCatOculos from "@/assets/banner-cat-oculos.webp";
import bannerCatEquipamentos from "@/assets/banner-cat-equipamentos.webp";
import bannerCatLentes from "@/assets/banner-cat-lentes.webp";
import bannerCatAcessorios from "@/assets/banner-cat-acessorios.webp";
import kitEstojosPremium from "@/assets/kit-estojos-premium-2026-04.webp";
import armacaoPremium from "@/assets/armacao-premium.webp";
import oculosSolarAviador from "@/assets/oculos-solar-aviador.webp";
import armacaoItaliana from "@/assets/armacao-italiana.webp";
import oculosSolarPolarizadoNew from "@/assets/oculos-solar-polarizado-new.webp";
import armacaoTitanio from "@/assets/armacao-titanio.webp";
import { useSiteAssetMap } from "@/hooks/useSiteAssets";
import { imgCard, imgDetail } from "@/lib/imageUrl";
import { BeholdInstagramFeed } from "@/components/home/BeholdInstagramFeed";
import { MaisLidos } from "@/components/blog/MaisLidos";
import { GoogleReviews } from "@/components/GoogleReviews";
import { useIsMobile } from "@/hooks/use-mobile";
import { FaqSection } from "@/components/seo/FaqSection";
import { FAQ_GERAL } from "@/lib/faqGeral";


/* ─── Event tracking ───────────────────────────────────────────────── */
function trackEvent(name: string, params?: Record<string, string>) {
  if (typeof window !== "undefined" && (window as any).gtag) {
    (window as any).gtag("event", name, params);
  }
}

/* ─── Animated counter ─────────────────────────────────────────────── */
function AnimatedCounter({ value, suffix }: { value: number; suffix: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStarted(true); }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!started) return;
    let cur = 0;
    const step = value / (1800 / 16);
    const t = setInterval(() => {
      cur += step;
      if (cur >= value) { setCount(value); clearInterval(t); }
      else setCount(Math.floor(cur));
    }, 16);
    return () => clearInterval(t);
  }, [started, value]);
  return <span ref={ref}>{count.toLocaleString("pt-BR")}{suffix}</span>;
}

/* ─── Icon map ──────────────────────────────────────────────────────── */
type LucideIcon = React.ComponentType<React.SVGProps<SVGSVGElement> & { size?: number | string }>;
const iconMap: Record<string, LucideIcon> = { Truck, BadgePercent, Shield, HeartHandshake };

/* ─── Fallback data ─────────────────────────────────────────────────── */
const fallbackOffers = [
  { tag: "Melhores Preços", badge: "15% OFF", title: "Óculos Completo", from: "a partir de", price: "R$ 20,00", img: bannerOculosCompleto, bg: "bg-accent", to: "/catalogo?categoria=oculos" },
  { tag: "Lançamento", badge: "10% OFF", title: "Óculos Solar Polarizado", from: "a partir de", price: "R$ 34,90", img: oculosSolarPolarizadoNew, bg: "bg-primary", to: "/catalogo?categoria=oculos&busca=solar" },
  { tag: "Private Label", badge: "NOVO", title: "Armação Italiana", from: "a partir de", price: "R$ 29,90", img: armacaoItaliana, bg: "bg-accent", to: "/catalogo?categoria=oculos&busca=acetato" },
  { tag: "Kit Promo", badge: "30% OFF", title: "Kit Estojos Premium", from: "a partir de", price: "R$ 9,90", img: kitEstojosPremium, bg: "bg-primary", to: "/catalogo?categoria=acessorios" },
];

const fallbackBenefits = [
  { icon: "Truck", label: "Entrega Rápida", sub: "Frete grátis acima de R$500" },
  { icon: "BadgePercent", label: "Preço de Atacado", sub: "Até 40% abaixo da tabela" },
  { icon: "Shield", label: "Garantia Total", sub: "Devolução em 7 dias" },
  { icon: "HeartHandshake", label: "Suporte no WhatsApp", sub: "Atendimento seg. a sex." },
];

const fallbackBrands = ["ACUVUE", "BAUSCH+LOMB", "CooperVision", "Alcon", "Solótica", "Oakley"];

const fallbackReviews = [
  { text: "Desde que comecei a comprar no Atacado Óptico, minha margem de lucro aumentou muito. Qualidade impecável e entrega sempre no prazo.", name: "Maria C.", time: "há 2 semanas" },
  { text: "A personalização de marca própria transformou minha óptica! Clientes adoram os produtos exclusivos. Atendimento excelente do começo ao fim.", name: "Carlos S.", time: "há 1 mês" },
  { text: "Melhor atacadista óptico de BH. Variedade enorme, preços justos e equipe super atenciosa. Recomendo sem hesitar para qualquer óptica.", name: "Ana O.", time: "há 3 semanas" },
];

const fallbackStats = [
  { value: 500, suffix: "+", label: "Ópticas Atendidas" },
  { value: 10000, suffix: "+", label: "Armações Vendidas" },
  { value: 15, suffix: "+", label: "Anos de Mercado" },
  { value: 100, suffix: "%", label: "Satisfação" },
];

const fallbackInstagram = [
  armacaoItaliana,
  "https://images.pexels.com/photos/5752305/pexels-photo-5752305.jpeg?auto=compress&cs=tinysrgb&w=200",
  "https://images.pexels.com/photos/1627639/pexels-photo-1627639.jpeg?auto=compress&cs=tinysrgb&w=200",
  "https://images.pexels.com/photos/5766154/pexels-photo-5766154.jpeg?auto=compress&cs=tinysrgb&w=200",
  oculosSolarPolarizadoNew,
  armacaoTitanio,
];

const fallbackPartnerCta = {
  title_line1: "Vamos abrir os",
  title_line2: "olhos do mundo juntos",
  description: "Torne-se um parceiro do Atacado Óptico e acesse os melhores produtos, preços exclusivos e suporte especializado.",
  button_text: "Seja nosso parceiro",
  button_link: "/crie-sua-marca",
  img: "https://images.pexels.com/photos/5766154/pexels-photo-5766154.jpeg?auto=compress&cs=tinysrgb&w=700",
};


const fallbackProducts = [
  { name: "Armação Acetato Premium", ref: "ARM-001", price: 89.90, oldPrice: 119.90, badge: "Lançamento", img: armacaoPremium, colors: ["#1a2744","#e87722","#888","#fff"] },
  { name: "Óculos Solar Aviador", ref: "SOL-012", price: 64.90, oldPrice: 89.90, badge: "Oferta", img: oculosSolarAviador, colors: ["#000","#c8a84b","#888"] },
  { name: "Armação Titânio Fino", ref: "TIT-034", price: 129.90, oldPrice: 179.90, badge: "Premium", img: armacaoTitanio, colors: ["#c0c0c0","#c8a84b","#1a2744"] },
  { name: "Óculos Solar Cat Eye", ref: "CAT-008", price: 49.90, oldPrice: 69.90, badge: "Tendência", img: "https://images.pexels.com/photos/8562440/pexels-photo-8562440.jpeg?auto=compress&cs=tinysrgb&w=400", colors: ["#000","#8B0000","#1a2744"] },
  { name: "Armação Kids Flex", ref: "KID-021", price: 29.90, oldPrice: 44.90, badge: "Kids", img: "https://images.pexels.com/photos/1627639/pexels-photo-1627639.jpeg?auto=compress&cs=tinysrgb&w=400", colors: ["#e87722","#4169E1","#228B22"] },
];

const fallbackCatBanners = [
  { name: "ÓCULOS", to: "/catalogo?categoria=oculos", img: "https://images.pexels.com/photos/1627639/pexels-photo-1627639.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "LENTES", to: "/simulador-publico", img: "https://images.pexels.com/photos/5752305/pexels-photo-5752305.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "ACESSÓRIOS", to: "/catalogo?categoria=acessorios", img: "https://images.pexels.com/photos/5201902/pexels-photo-5201902.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "EQUIPAMENTOS", to: "/catalogo?categoria=equipamentos", img: "https://images.pexels.com/photos/5752309/pexels-photo-5752309.jpeg?auto=compress&cs=tinysrgb&w=600" },
];

/* ─── Product card ──────────────────────────────────────────────────── */
type ProductItem = { name: string; ref: string; price: number; oldPrice: number; badge?: string; img: string; colors: string[] | string; to?: string };
function ProductCard({ p }: { p: ProductItem }) {
  const { toggleFavorite, isFavorite } = useFavorites();
  const pct = p.oldPrice > 0 ? Math.round((1 - p.price / p.oldPrice) * 100) : 0;
  const colorList: string[] = typeof p.colors === "string" ? p.colors.split(",").map((c: string) => c.trim()) : (p.colors || []);
  const slugOrId = (p as any).slug ?? (p as any).id;
  const to = p.to ?? (slugOrId ? `/produto/${slugOrId}` : `/catalogo?busca=${encodeURIComponent(p.ref)}`);
  const liked = isFavorite((p as any).id || p.name);
  return (
    <div className="relative flex-shrink-0 w-48 md:w-52 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow group overflow-hidden">
      <Link to={to} className="block">
      <div className="relative h-40 bg-gray-50 overflow-hidden">
        <img src={p.img} alt={p.name} loading="lazy" decoding="async" width={208} height={160} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-2 left-2 flex gap-1">
          {p.badge && <span className="bg-primary text-white text-[9px] font-bold px-2 py-0.5 rounded">{p.badge}</span>}
          {pct > 0 && <span className="bg-accent text-white text-[9px] font-bold px-2 py-0.5 rounded">{pct}% OFF</span>}
        </div>
        <button
          onClick={(e) => { e.preventDefault(); toggleFavorite({ productId: (p as any).id || p.name, name: p.name, reference: p.ref, price: p.price, imageUrl: p.img }); }}
          className="absolute top-0 right-0 h-11 w-11 flex items-center justify-center"
          aria-label={liked ? "Remover dos favoritos" : "Adicionar aos favoritos"}
          aria-pressed={liked}
        >
          <span aria-hidden="true" className="h-7 w-7 rounded-full bg-white/80 flex items-center justify-center shadow-sm">
            <Heart className={`h-3.5 w-3.5 transition-colors ${liked ? "fill-red-500 text-red-500" : "text-gray-400"}`} />
          </span>

        </button>
      </div>
      <div className="p-3">
        <p className="text-[10px] text-muted-foreground mb-0.5">{p.ref}</p>
        <p className="text-xs font-semibold text-foreground leading-tight mb-2 line-clamp-2">{p.name}</p>
        <div className="flex gap-1 mb-1" aria-hidden="true">
          {[1,2,3,4,5].map(i => <Star key={i} className={`h-3 w-3 ${i <= 4 ? "fill-amber-400 text-amber-400" : "text-gray-200"}`} />)}
          <span className="text-[10px] text-muted-foreground ml-0.5">48</span>
        </div>
        {p.price > 0 ? (
          <>
            <p className="text-accent font-black text-base">R$ {p.price.toFixed(2)}</p>
            {p.oldPrice > p.price && <p className="text-[11px] text-muted-foreground line-through">R$ {p.oldPrice.toFixed(2)}</p>}
          </>
        ) : (
          <p className="text-accent font-black text-base">Ver detalhes</p>
        )}
        <div className="flex gap-1 mt-2">
          {colorList.map((c, i) => (
            <span key={i} className="h-3.5 w-3.5 rounded-full border border-gray-200 shadow-sm" style={{ background: c }} />
          ))}
        </div>
      </div>
      </Link>
      <div
        className="absolute inset-x-0 bottom-0 bg-accent text-white text-[11px] font-bold py-2 text-center translate-y-full group-hover:translate-y-0 transition-transform duration-200 pointer-events-none"
        aria-hidden="true"
      >
        Ver Produto
      </div>
    </div>
  );
}

/* ─── Horizontal scroll section ─────────────────────────────────────── */
function ScrollSection({
  title, link, children, loading = false, skeletonCount = 4,
}: {
  title: string; link?: string; children: React.ReactNode; loading?: boolean; skeletonCount?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <section className="py-10">
      <div className="container">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-lg md:text-xl font-bold text-[#1A3870]">{title}</h2>
          <div className="flex items-center gap-2">
            {link && <Link to={link} aria-label={`Ver todos os produtos de ${title}`} className="text-accent text-sm font-medium hover:underline inline-flex items-center gap-1 min-h-11 py-2">Ver tudo <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" /></Link>}
            <button onClick={() => ref.current?.scrollBy({ left: -220, behavior: "smooth" })} className="h-11 w-11 shrink-0 rounded-full border border-gray-200 flex items-center justify-center hover:border-accent hover:text-accent transition-colors text-muted-foreground" aria-label="Rolar para a esquerda">
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <button onClick={() => ref.current?.scrollBy({ left: 220, behavior: "smooth" })} className="h-11 w-11 shrink-0 rounded-full border border-gray-200 flex items-center justify-center hover:border-accent hover:text-accent transition-colors text-muted-foreground" aria-label="Rolar para a direita">
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div ref={ref} className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollSnapType: "x mandatory" }}>
          {loading
            ? Array.from({ length: skeletonCount }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-48 md:w-52 rounded-xl bg-gray-100 animate-pulse" style={{ height: 240 }} aria-hidden="true" />
              ))
            : children}
        </div>
      </div>
    </section>
  );
}

/* ─── Lazy section mount ─────────────────────────────────────────────── */
function LazySection({
  children, minHeight = "200px", className, rootMargin = "400px",
}: {
  children: React.ReactNode; minHeight?: string; className?: string; rootMargin?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible, rootMargin]);
  return (
    <div ref={ref} className={className} style={!visible ? { minHeight } : undefined}>
      {visible ? children : null}
    </div>
  );
}

/* ─── FAQ ───────────────────────────────────────────────────────────── */
const FAQ_ITEMS = [
  { q: "O Atacado Óptico atende somente lojistas?", a: "Sim, somos um atacadista focado em atender ópticas, clínicas de oftalmologia e revendedores. Para acesso ao catálogo completo com preços e pedidos, é necessário CNPJ ativo no setor óptico." },
  { q: "Vocês enviam para todo o Brasil?", a: "Sim! Atendemos ópticas em todos os estados brasileiros. O frete é calculado no momento do pedido e temos parceria com as principais transportadoras do país." },
  { q: "Como me cadastro para ver os preços?", a: "Clique em 'Cadastrar' no menu superior, preencha os dados do seu CNPJ e aguarde a aprovação. Após aprovado, você terá acesso imediato a todos os preços e pode fazer pedidos online." },
  { q: "O Atacado Óptico faz marca própria (Private Label)?", a: "Sim! Temos um programa completo de marca própria onde personalizamos armações e acessórios com o logotipo da sua óptica. Consulte a página 'Crie Sua Marca' para saber mais." },
  { q: "Qual é o pedido mínimo?", a: "Trabalhamos com pedido mínimo a partir de R$ 500. Para programas de marca própria, o mínimo varia conforme o produto. Entre em contato pelo WhatsApp para uma proposta personalizada." },
  { q: "Vocês trabalham com lentes oftálmicas no atacado?", a: "Sim! Temos catálogo completo de lentes oftálmicas: monofocal, multifocal, fotossensível, antirreflexo e lentes solares, das marcas Hoya, Essilor e Transitions." },
];

function HomeFAQ() {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <section className="bg-white py-12">
      <div className="container max-w-3xl">
        <h2 className="font-heading text-lg md:text-xl font-bold text-foreground mb-2">Perguntas Frequentes</h2>
        <p className="text-sm text-muted-foreground mb-8">Tudo o que você precisa saber sobre o Atacado Óptico</p>
        <div className="space-y-3">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                aria-expanded={open === i}
              >
                <span className="font-heading font-semibold text-sm text-foreground">{item.q}</span>
                <ChevronRight className={`h-4 w-4 text-accent shrink-0 transition-transform ${open === i ? "rotate-90" : ""}`} aria-hidden="true" />
              </button>
              {open === i && (
                <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-gray-50">
                  <p className="pt-3">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Page ──────────────────────────────────────────────────────────── */
export default function Home() {
  const isMobile = useIsMobile();
  const siteAssets = useSiteAssetMap([
    "icon-home-oculos", "icon-home-lentes", "icon-home-acessorios",
    "icon-home-equipamentos", "icon-home-pedidolab",
    "banner-oculos-completo", "banner-oculos-solar",
    "banner-cat-oculos", "banner-cat-equipamentos", "banner-cat-lentes", "banner-cat-acessorios",
    "editorial-armacao-italiana", "editorial-armacao-premium", "editorial-armacao-titanio",
    "editorial-kit-estojos", "editorial-oculos-solar-aviador", "editorial-oculos-solar-polarizado",
  ]);

  // Passa pelo redimensionador do storage: evita baixar originais de 2 MB na home.
  const a = (key: string, fallback: string) => imgCard(siteAssets[key] ?? fallback) || fallback;

  const categories = [
    { name: "Óculos de Grau", icon: ({ className }: { className?: string }) => <img src={a("icon-home-oculos", oculosHomeIcon)} alt="Óculos de Grau" width={28} height={28} loading="lazy" decoding="async" className={(className ?? "") + " object-contain"} />, to: "/catalogo?categoria=oculos" },
    { name: "Lentes", icon: ({ className }: { className?: string }) => <img src={a("icon-home-lentes", lentesHomeIcon)} alt="Lentes" width={28} height={28} loading="lazy" decoding="async" className={(className ?? "") + " object-contain"} />, to: "/lentes-grade" },
    { name: "Acessórios", icon: ({ className }: { className?: string }) => <img src={a("icon-home-acessorios", acessoriosHomeIcon)} alt="Acessórios" width={28} height={28} loading="lazy" decoding="async" className={(className ?? "") + " object-contain"} />, to: "/catalogo?categoria=acessorios" },
    { name: "Equipamentos", icon: ({ className }: { className?: string }) => <img src={a("icon-home-equipamentos", equipamentosHomeIcon)} alt="Equipamentos" width={28} height={28} loading="lazy" decoding="async" className={(className ?? "") + " object-contain"} />, to: "/catalogo?categoria=equipamentos" },
    { name: "Pedido ao Lab", icon: ({ className }: { className?: string }) => <img src={a("icon-home-pedidolab", pedidolabHomeIcon)} alt="Pedido ao Lab" width={28} height={28} loading="lazy" decoding="async" className={(className ?? "") + " object-contain"} />, to: "/pedido-laboratorio" },
  ];

  usePageMeta({
    title: "Atacado Óptico - Catálogo para Ópticas",
    description: "Atacado Óptico: óculos, acessórios, equipamentos e lentes para sua loja. Personalização de marca própria. Entrega rápida para todo o Brasil.",
    canonical: "https://www.atacadooptico.com.br/",
    ogImage: "https://www.atacadooptico.com.br/og-image.png",
  });

  useEffect(() => {
    const siteUrl = "https://www.atacadooptico.com.br";
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": ["LocalBusiness", "WholesaleStore"],
        "name": "Atacado Óptico",
        "description": "Atacadista de óculos, armações, lentes, acessórios e equipamentos ópticos. Atende ópticas em todo o Brasil com marca própria (Private Label).",
        "url": siteUrl,
        "telephone": "+55-31-98501-5555",
        "email": "atendimento@atacadooptico.com.br",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Av. Afonso Pena, 726, Sala 800",
          "addressLocality": "Belo Horizonte",
          "addressRegion": "MG",
          "postalCode": "30130-902",
          "addressCountry": "BR",
        },
        "areaServed": { "@type": "Country", "name": "Brasil" },
        "foundingDate": "2009",
        "priceRange": "$$",
        "openingHours": "Mo-Fr 08:00-18:00",
        "sameAs": ["https://www.instagram.com/atacadooptico"],
        "knowsAbout": [
          "Óculos de Grau Atacado",
          "Lentes Oftálmicas",
          "Armações de Óculos",
          "Marca Própria Private Label",
          "Equipamentos Ópticos",
          "Acessórios para Ópticas",
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Catálogo Atacado Óptico",
          "url": `${siteUrl}/catalogo`,
        },
        "logo": { "@type": "ImageObject", "url": `${siteUrl}/logo.png` },
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Atacado Óptico",
        "url": siteUrl,
        "description": "Atacadista óptico: óculos, lentes, armações e equipamentos para ópticas em todo o Brasil.",
        "inLanguage": "pt-BR",
        "potentialAction": {
          "@type": "SearchAction",
          "target": { "@type": "EntryPoint", "urlTemplate": `${siteUrl}/catalogo?q={search_term_string}` },
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "O Atacado Óptico atende somente lojistas?", "acceptedAnswer": { "@type": "Answer", "text": "Sim, somos um atacadista focado em atender ópticas, clínicas de oftalmologia e revendedores. Para acesso ao catálogo completo com preços e pedidos, é necessário CNPJ ativo no setor óptico." } },
          { "@type": "Question", "name": "Vocês enviam para todo o Brasil?", "acceptedAnswer": { "@type": "Answer", "text": "Sim! Atendemos ópticas em todos os estados brasileiros. O frete é calculado no momento do pedido e temos parceria com as principais transportadoras do país." } },
          { "@type": "Question", "name": "Como me cadastro para ver os preços?", "acceptedAnswer": { "@type": "Answer", "text": "Clique em 'Cadastrar' no menu superior, preencha os dados do seu CNPJ e aguarde a aprovação. Após aprovado, você terá acesso imediato a todos os preços e pode fazer pedidos online." } },
          { "@type": "Question", "name": "O Atacado Óptico faz marca própria (Private Label)?", "acceptedAnswer": { "@type": "Answer", "text": "Sim! Temos um programa completo de marca própria onde personalizamos armações e acessórios com o logotipo da sua óptica. Consulte a página 'Crie Sua Marca' para saber mais." } },
          { "@type": "Question", "name": "Qual é o pedido mínimo?", "acceptedAnswer": { "@type": "Answer", "text": "Trabalhamos com pedido mínimo a partir de R$ 500. Para programas de marca própria, o mínimo varia conforme o produto. Entre em contato pelo WhatsApp para uma proposta personalizada." } },
          { "@type": "Question", "name": "Onde o Atacado Óptico está localizado?", "acceptedAnswer": { "@type": "Answer", "text": "Estamos localizados na Av. Afonso Pena, 726, Sala 800, no Centro de Belo Horizonte (próximo à Praça Sete), MG. Nosso horário de atendimento é segunda a sexta, das 8h às 18h." } },
          { "@type": "Question", "name": "Vocês trabalham com lentes oftálmicas no atacado?", "acceptedAnswer": { "@type": "Answer", "text": "Sim! Temos catálogo completo de lentes oftálmicas: monofocal, multifocal, fotossensível, antirreflexo e lentes solares, das marcas Hoya, Essilor e Transitions." } },
        ],
      },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "home-schema";
    script.text = JSON.stringify(schemas);
    document.head.appendChild(script);
    return () => { document.getElementById("home-schema")?.remove(); };
  }, []);

  const { data: sections, isLoading: sectionsLoading } = useHomeSections();
  const benefits = (sections?.get("benefits") as typeof fallbackBenefits) || fallbackBenefits;
  const offerLocalImageMap: Record<string, string> = {
    "Óculos Completo": a("banner-oculos-completo", bannerOculosCompleto),
    "Óculos Solar Polarizado": a("editorial-oculos-solar-polarizado", oculosSolarPolarizadoNew),
    "Armação Italiana": a("editorial-armacao-italiana", armacaoItaliana),
    "Kit Estojos Premium": a("editorial-kit-estojos", kitEstojosPremium),
  };
  const offerLinkMap: Record<string, string> = {
    "Óculos Completo": "/catalogo?categoria=oculos",
    "Óculos Solar Polarizado": "/catalogo?categoria=oculos&busca=solar",
    "Armação Italiana": "/catalogo?categoria=oculos&busca=acetato",
    "Kit Estojos Premium": "/catalogo?categoria=acessorios",
  };
  const rawOffers = (sections?.get("offers") as typeof fallbackOffers) || fallbackOffers;
  const offers = rawOffers.map((o) => ({
    ...o,
    img: offerLocalImageMap[o.title] || imgCard(o.img) || o.img,
    to: (o as any).to || offerLinkMap[o.title] || "/catalogo",
  }));
  const brands = (sections?.get("brands") as typeof fallbackBrands) || fallbackBrands;
  const manualReviews = (sections?.get("reviews") as typeof fallbackReviews) || fallbackReviews;
  const stats = (sections?.get("stats") as typeof fallbackStats) || fallbackStats;
  const instagramImageMap: Record<string, string> = {
    "https://images.pexels.com/photos/5754242/pexels-photo-5754242.jpeg?auto=compress&cs=tinysrgb&w=200": armacaoItaliana,
    "https://images.pexels.com/photos/2371968/pexels-photo-2371968.jpeg?auto=compress&cs=tinysrgb&w=200": oculosSolarPolarizadoNew,
    "https://images.pexels.com/photos/5201895/pexels-photo-5201895.jpeg?auto=compress&cs=tinysrgb&w=200": armacaoTitanio,
  };
  const rawInstagramImages = (sections?.get("instagram_images") as typeof fallbackInstagram) || fallbackInstagram;
  const instagramImages = rawInstagramImages.map((img) => instagramImageMap[img] || img);
  const partnerCta = (sections?.get("partner_cta") as typeof fallbackPartnerCta) || fallbackPartnerCta;
  const catLocalImageMap: Record<string, string> = {
    "ÓCULOS": a("banner-cat-oculos", bannerCatOculos),
    "EQUIPAMENTOS": a("banner-cat-equipamentos", bannerCatEquipamentos),
    "LENTES": a("banner-cat-lentes", bannerCatLentes),
    "ACESSÓRIOS": a("banner-cat-acessorios", bannerCatAcessorios),
  };
  const rawCatBanners = (sections?.get("category_banners") as typeof fallbackCatBanners) || fallbackCatBanners;
  const catBanners = rawCatBanners.map((b) => ({ ...b, img: catLocalImageMap[b.name] || imgCard(b.img) || b.img }));
  const featuredProductImageMap: Record<string, string> = {
    "Armação Acetato Premium": a("editorial-armacao-premium", armacaoPremium),
    "Óculos Solar Aviador": a("editorial-oculos-solar-aviador", oculosSolarAviador),
    "Armação Titânio Fino": a("editorial-armacao-titanio", armacaoTitanio),
  };
  const rawFeaturedProducts = (sections?.get("featured_products") as typeof fallbackProducts) || fallbackProducts;
  const featuredRefs = useMemo(() => rawFeaturedProducts.map((p) => p.ref).filter(Boolean), [rawFeaturedProducts]);
  const { data: refToSlug } = useQuery({
    queryKey: ["home-featured-refs", featuredRefs.sort().join(",")],
    enabled: featuredRefs.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("products")
        .select("id, slug, reference")
        .in("reference", featuredRefs);
      const map: Record<string, string> = {};
      (data || []).forEach((p: any) => { map[p.reference] = p.slug || p.id; });
      return map;
    },
  });
  const featuredProducts = rawFeaturedProducts.map((product) => ({
    ...product,
    img: featuredProductImageMap[product.name] || imgCard(product.img) || product.img,
    slug: refToSlug?.[product.ref],
  }));

  // Top 1 produto por categoria para seção "Mais vendidos"
  const topCategories = ["oculos", "lentes", "acessorios", "equipamentos", "insumos", "fornitura"];
  const { data: topByCategory } = useQuery({
    queryKey: ["home-top-by-category", topCategories.join(",")],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const results: ProductItem[] = [];
      for (const cat of topCategories) {
        const { data } = await supabase
          .from("products")
          .select("id, slug, reference, name, price, list_price, category, is_featured, product_images(image_url, sort_order)")
          .eq("active", true)
          .eq("category", cat)
          .gt("price", 5)
          .order("is_featured", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(1);
        const p: any = data?.[0];
        if (!p) continue;
        const imgs = (p.product_images || []).slice().sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        const img = imgCard(imgs[0]?.image_url) || "/placeholder.svg";
        const price = Number(p.price) || 0;
        const oldPrice = Number(p.list_price) > price ? Number(p.list_price) : price * 1.2;
        results.push({
          name: p.name,
          ref: p.reference,
          price,
          oldPrice,
          img,
          colors: [],
          badge: cat.charAt(0).toUpperCase() + cat.slice(1),
          ...({ slug: p.slug || p.id, id: p.id } as any),
        });
      }
      return results;
    },
  });

  // Mais vendidos em lentes: 4 produtos reais + Surfaçada + Simulador = 6 opções
  const { data: topLenses } = useQuery({
    queryKey: ["home-top-lenses"],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("products")
        .select("id, slug, reference, name, price, list_price, category, product_images(image_url, sort_order)")
        .eq("active", true)
        .in("category", ["lentes", "lentes-contato"])
        .gt("price", 0)
        .not("slug", "in", "(airclear-mensal)")
        .order("price", { ascending: false })
        .limit(4);
      return (data || []).map((p: any) => {
        const imgs = (p.product_images || []).slice().sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        const price = Number(p.price) || 0;
        const oldPrice = Number(p.list_price) > price ? Number(p.list_price) : price * 1.2;
        return {
          name: p.name,
          ref: p.reference,
          price,
          oldPrice,
          img: imgCard(imgs[0]?.image_url) || "/placeholder.svg",
          colors: [],
          badge: p.category === "lentes-contato" ? "Contato" : "Lentes",
          ...({ slug: p.slug || p.id, id: p.id } as any),
        } as ProductItem;
      });
    },
  });
  const googleConfig = (sections?.get("google_config") as { place_id?: string; maps_url?: string } | undefined);

  const { reviews: googleReviews, rating: googleRating, configured: googleConfigured } = useGoogleReviews(googleConfig?.place_id);
  const reviews = googleConfigured && googleReviews.length > 0 ? googleReviews : manualReviews;
  const displayRating = googleConfigured && googleRating ? googleRating.toFixed(1).replace(".", ",") : "4,9";

  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterDone, setNewsletterDone] = useState(false);

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterLoading(true);
    try {
      const { error } = await supabase.from("newsletter_subscribers").insert({ email: newsletterEmail.trim().toLowerCase() });
      if (error && error.code === "23505") {
        toast.info("E-mail já cadastrado. Você já receberá nossas ofertas!");
      } else if (error) {
        throw error;
      } else {
        setNewsletterDone(true);
        toast.success("Inscrito! Em breve você receberá nossas ofertas.");
      }
    } catch {
      toast.error("Erro ao inscrever. Tente novamente.");
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <div className="bg-[#f5f6f8] min-h-screen">
      <h1 className="sr-only">
        Atacado Óptico: lentes, armações, acessórios e equipamentos no atacado para ópticas em todo o Brasil
      </h1>
      <Helmet>

        <title>Atacado Óptico | Lentes e Armações no Atacado — Belo Horizonte, MG</title>
        <meta name="description" content="O maior distribuidor de lentes e armações do Brasil. Essilor, Zeiss, Hoya, bifocais, altas dioptrias e cristal mineral. Atacadista em BH com entrega nacional, com mais de uma década de especialização." />
        <link rel="canonical" href="https://www.atacadooptico.com.br/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.atacadooptico.com.br/" />
        <meta property="og:title" content="Atacado Óptico | Maior Distribuidor de Lentes do Brasil" />
        <meta property="og:description" content="Essilor, Zeiss, Hoya, bifocais, altas dioptrias e cristal mineral. 10.000+ produtos. Atacadista em BH com entrega nacional." />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Atacado Óptico" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": ["LocalBusiness", "WholesaleStore"],
              "@id": "https://www.atacadooptico.com.br/#organization",
              "name": "Atacado Óptico",
              "url": "https://www.atacadooptico.com.br",
              "logo": "https://www.atacadooptico.com.br/logo.png",
              "description": "Maior distribuidor atacadista de lentes oftálmicas e armações do Brasil. Especialização em altas dioptrias, lentes bifocais, cristal mineral. Distribuidor Essilor, Zeiss e Hoya.",
              "telephone": "+55-31-98501-5555",
              "email": "atendimento@atacadooptico.com.br",
              "foundingDate": "2015",
              "address": { "@type": "PostalAddress", "streetAddress": "Av. Afonso Pena, 726, Sala 800", "addressLocality": "Belo Horizonte", "addressRegion": "MG", "postalCode": "30130-003", "addressCountry": "BR" },
              "geo": { "@type": "GeoCoordinates", "latitude": -19.9191, "longitude": -43.9386 },
              "openingHoursSpecification": [{ "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "08:00", "closes": "18:00" }],
              "priceRange": "$$",
              "areaServed": { "@type": "Country", "name": "Brasil" },
              "knowsAbout": ["lentes oftálmicas","lentes altas dioptrias","lentes bifocais","cristal mineral","Essilor","Zeiss","Hoya","armações de grau","óculos solares","distribuição atacadista óptica"],
              "hasOfferCatalog": { "@type": "OfferCatalog", "name": "Catálogo Atacado Óptico", "numberOfItems": 10000 }
            },
            {
              "@type": "WebSite",
              "@id": "https://www.atacadooptico.com.br/#website",
              "url": "https://www.atacadooptico.com.br",
              "name": "Atacado Óptico",
              "publisher": { "@id": "https://www.atacadooptico.com.br/#organization" },
              "potentialAction": { "@type": "SearchAction", "target": "https://www.atacadooptico.com.br/catalogo?q={search_term_string}", "query-input": "required name=search_term_string" }
            }
          ]
        })}</script>
      </Helmet>

      <PromoBanner />


      {/* ── BENEFITS STRIP ───────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {sectionsLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className={`flex items-center gap-3 px-5 py-4 ${i < 3 ? "border-r border-gray-100" : ""}`} aria-hidden="true">
                    <div className="h-5 w-5 rounded bg-gray-200 animate-pulse shrink-0" />
                    <div className="space-y-1.5">
                      <div className="h-3 w-20 bg-gray-200 animate-pulse rounded" />
                      <div className="h-2.5 w-14 bg-gray-100 animate-pulse rounded" />
                    </div>
                  </div>
                ))
              : benefits.map((b: any, i: number) => {
                  const Icon = iconMap[b.icon] || Truck;
                  return (
                    <div key={i} className={`flex items-center gap-3 px-5 py-4 ${i < 3 ? "border-r border-gray-100" : ""}`}>
                      <Icon className="h-5 w-5 text-accent shrink-0" aria-hidden="true" />
                      <div>
                        <p className="font-heading font-bold text-foreground text-xs">{b.label}</p>
                        <p className="text-[11px] text-muted-foreground">{b.sub}</p>
                      </div>
                    </div>
                  );
                })}
          </div>
        </div>
      </div>

      {/* ── OFFER BANNERS ────────────────────────────────────────────── */}
      <section className="container py-6" aria-label="Melhores ofertas da semana">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg md:text-xl font-bold text-[#1A3870]">Melhores ofertas da semana</h2>
          <Link to="/catalogo" aria-label="Ver todo o catálogo de produtos" className="text-accent text-sm font-medium hover:underline inline-flex items-center gap-1 min-h-11 py-2">Ver tudo <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {sectionsLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-2xl h-44 md:h-52 bg-gray-200 animate-pulse" aria-hidden="true" />
              ))
            : offers.map((o, i) => (
                <Reveal key={i} immediate delay={i * 0.08}>
                  <Link to={(o as any).to || "/catalogo"}>
                    <div className={`relative ${o.bg} rounded-2xl overflow-hidden h-44 md:h-52 group cursor-pointer`}>
                      <img src={o.img} alt={o.title} loading="lazy" decoding="async" width={400} height={208} className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 transition-opacity" />
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30" />
                      <div className="relative z-10 p-4 h-full flex flex-col justify-between">
                        <div>
                          <span className="inline-block bg-white/20 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-2">{o.badge}</span>
                          <p className="text-white/80 text-[11px] font-medium">{o.tag}</p>
                          <p className="text-white font-heading font-black text-sm md:text-base leading-tight">{o.title}</p>
                        </div>
                        <div>
                          <p className="text-white/70 text-[10px]">{o.from}</p>
                          <p className="text-white font-heading font-black text-xl md:text-2xl">{o.price}</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
        </div>
      </section>

      {/* ── CATEGORY ICONS ───────────────────────────────────────────── */}
      <section className="bg-white py-6 border-y border-gray-100" aria-label="Compre por Categoria">
        <div className="container">
          <p className="text-sm font-heading font-bold text-foreground mb-4" aria-hidden="true">Compre por Categoria</p>
          <div className="flex gap-6 md:gap-10 overflow-x-auto scrollbar-hide pb-1">
            {categories.map((cat, i) => (
              <Link key={i} to={cat.to} className="flex flex-col items-center gap-2 shrink-0 group">
                <div className="h-14 w-14 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors ring-2 ring-transparent group-hover:ring-accent/30">
                  <cat.icon className="h-7 w-7 text-accent" />
                </div>
                <span className="text-[11px] font-medium text-foreground group-hover:text-accent transition-colors text-center whitespace-nowrap">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── MAIS VENDIDOS (1 por categoria) ─────────────────────────── */}
      <div className="bg-[#f5f6f8]">
        <ScrollSection title="Mais vendidos" link="/catalogo" loading={sectionsLoading}>
          {(topByCategory && topByCategory.length > 0 ? topByCategory : featuredProducts).map((p, i) => (
            <div key={i} style={{ scrollSnapAlign: "start" }}>
              <ProductCard p={p} />
            </div>
          ))}
        </ScrollSection>
      </div>

      {/* ── CATEGORY BANNERS 2x2 ─────────────────────────────────────── */}
      <section className="container pb-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {catBanners.map((b, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <Link to={b.to} className="relative block h-36 md:h-48 rounded-2xl overflow-hidden group">
                <img src={b.img} alt={b.name} loading="lazy" decoding="async" width={400} height={192} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <p className="font-heading font-black text-white text-lg md:text-xl tracking-wide">{b.name}</p>
                  <div className="h-0.5 w-8 bg-accent mt-1" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── MAIS VENDIDOS LENTES ─────────────────────────────────────── */}
      <div className="bg-white">
        <ScrollSection title="Mais vendidos em lentes" link="/lentes-grade" loading={sectionsLoading}>
          {(() => {
            const TOTAL = 6;
            const surfacadoCard: ProductItem = {
              name: "Lente Surfaçada sob medida",
              ref: "SURFACADO",
              price: 0,
              oldPrice: 0,
              img: a("editorial-armacao-titanio", armacaoTitanio),
              colors: [],
              badge: "Sob medida",
              ...({ to: "/surfacado" } as any),
            } as ProductItem;
            const simuladorCard: ProductItem = {
              name: "Simulador de Lentes",
              ref: "SIMULADOR",
              price: 0,
              oldPrice: 0,
              img: a("banner-cat-lentes", bannerCatLentes),
              colors: [],
              badge: "Ferramenta",
              ...({ to: "/simulador-publico" } as any),
            } as ProductItem;
            const fixed = [surfacadoCard, simuladorCard];
            const realSlots = TOTAL - fixed.length; // 4
            const real = (topLenses || []).slice(0, realSlots);
            // Fallback: se faltarem produtos reais, preenche com placeholders da própria lista
            // para garantir sempre exatamente 6 cards
            const padded: ProductItem[] = [...real];
            while (padded.length < realSlots && real.length > 0) {
              padded.push(real[padded.length % real.length]);
            }
            while (padded.length < realSlots) {
              padded.push(simuladorCard);
            }
            const cards = [...padded, ...fixed].slice(0, TOTAL);
            if (import.meta.env.DEV && cards.length !== TOTAL) {
              console.warn(`[Home] Mais vendidos em lentes: esperado ${TOTAL}, recebido ${cards.length}`);
            }
            return cards.map((p, i) => (
              <div key={`${p.ref}-${i}`} style={{ scrollSnapAlign: "start" }}>
                <ProductCard p={p} />
              </div>
            ));
          })()}
        </ScrollSection>
      </div>


      {/* ── STATS ────────────────────────────────────────────────────── */}
      <section className="bg-[#1A3870] py-12" aria-label="Números do Atacado Óptico">
        <div className="container grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((s: any, i: number) => (
            <Reveal key={i} delay={i * 0.1}>
              <p className="font-heading font-black text-4xl md:text-5xl text-[#F07230]">
                <AnimatedCounter value={s.value} suffix={s.suffix} />
              </p>
              <p className="text-white/75 text-sm mt-1">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── BRANDS ───────────────────────────────────────────────────── */}
      <section className="bg-white py-10 border-y border-gray-100" aria-label="Melhores marcas">
        <div className="container">
          <p className="font-heading font-bold text-foreground text-sm mb-6" aria-hidden="true">Melhores marcas</p>
          <div className="flex items-center gap-8 md:gap-12 overflow-x-auto scrollbar-hide pb-1">
            {brands.map((b: any, i: number) => {
              const brandName = typeof b === "string" ? b : b.name || b;
              const brandLink = typeof b === "object" && b.link ? b.link : `/catalogo?busca=${encodeURIComponent(brandName)}`;
              return (
                <Link key={i} to={brandLink} className="shrink-0 py-2 min-h-11 flex items-center font-heading font-black text-lg md:text-xl text-muted-foreground/40 hover:text-primary transition-colors whitespace-nowrap">
                  {brandName}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── GOOGLE REVIEWS ───────────────────────────────────────────── */}
      <LazySection minHeight="380px">
      <section className="py-12 bg-[#f5f6f8]">
        <div className="container">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="font-heading text-lg md:text-xl font-bold text-[#1A3870] mb-1">Avaliações no Google</h2>
              <div className="flex items-center gap-2">
                {/* Google "G" logo */}
                <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.59-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
                <span className="font-heading font-black text-2xl text-foreground">{displayRating}</span>
                <div className="flex gap-0.5" aria-hidden="true">
                  {[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
                </div>
                <span className="text-sm text-muted-foreground">no Google</span>
              </div>
            </div>
            <a
              href={googleConfig?.maps_url || "https://www.google.com/maps/place/Atacado+%C3%93ptico+Lentes,Arma%C3%A7%C3%A3o,+Fornitura+e+Acess%C3%B3rios+%C3%B3ptico/@-19.9197793,-43.9566736,15z/data=!4m10!1m2!2m1!1sAtacado+%C3%93ptico+Afonso+Pena+726+Belo+Horizonte!3m6!1s0xa699fb51549755:0x6b3bd110d92b2e0a!8m2!3d-19.9197793!4d-43.9386492!15sCi5BdGFjYWRvIMOTcHRpY28gQWZvbnNvIFBlbmEgNzI2IEJlbG8gSG9yaXpvbnRlWjAiLmF0YWNhZG8gw7NwdGljbyBhZm9uc28gcGVuYSA3MjYgYmVsbyBob3Jpem9udGWSAQhvcHRpY2lhbpoBI0NoWkRTVWhOTUc5blMwVkpRMEZuU1VOWWRDMVhWbUZCRUFF4AEA-gEECAAQRg!16s%2Fg%2F11c208wm8h?entry=ttu"}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm font-semibold text-[#4285F4] hover:text-[#1a73e8] transition-colors border border-[#4285F4]/30 hover:border-[#4285F4] px-4 py-2 rounded-full"
            >
              <svg width="14" height="14" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.59-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
              Ver todas as avaliações
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {reviews.map((t: any, i: number) => (
              <Reveal key={i} delay={i * 0.1}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-1" aria-hidden="true">
                    {[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
                  </div>
                  <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.59-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
                </div>
                <p className="text-foreground/70 text-sm leading-relaxed italic mb-5">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  {t.photoUrl && (
                    <img src={t.photoUrl} alt={t.name} loading="lazy" decoding="async" width={32} height={32} className="h-8 w-8 rounded-full object-cover shrink-0" referrerPolicy="no-referrer" />
                  )}
                  <div className="flex items-center justify-between flex-1">
                    <p className="font-heading font-bold text-sm text-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.time}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      </LazySection>

      {/* ── PARTNER CTA ──────────────────────────────────────────────── */}
      <LazySection minHeight="280px">
      <section className="bg-white">
        <div className="container py-0">
          <div className="relative overflow-hidden rounded-none md:rounded-2xl bg-gray-50 border border-gray-100">
            <div className="grid md:grid-cols-2">
              <div className="p-10 md:p-14 flex flex-col justify-center">
                <h2 className="font-heading font-black text-2xl md:text-3xl text-primary leading-tight mb-1">
                  {partnerCta.title_line1}
                </h2>
                <h2 className="font-heading font-black text-2xl md:text-3xl text-accent leading-tight mb-6">
                  {partnerCta.title_line2}
                </h2>
                <div className="h-0.5 w-16 bg-accent mb-6" />
                <p className="text-foreground/60 text-sm mb-8 max-w-sm leading-relaxed">
                  {partnerCta.description}
                </p>
                <Link to={partnerCta.button_link || "/crie-sua-marca"}>
                  <Button className="bg-accent hover:bg-accent/90 text-white font-heading font-bold px-8 h-12 w-fit">
                    {partnerCta.button_text} <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Button>
                </Link>
              </div>
              <div className="hidden md:block relative min-h-[280px]">
                <img
                  src={imgDetail(partnerCta.img) || partnerCta.img}
                  alt="Parceiros"
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={560}
                  className="absolute inset-0 w-full h-full object-cover"
                />

              </div>
            </div>
          </div>
        </div>
      </section>
      </LazySection>

      {/* ── MAIS LIDOS NO BLOG ───────────────────────────────────────── */}
      <LazySection minHeight="300px">
      <section className="bg-white py-12 border-t border-gray-100">
        <div className="container">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="font-heading text-lg md:text-xl font-bold text-[#1A3870] mb-1">Mais lidos no Blog</h2>
              <p className="text-sm text-muted-foreground">Conteúdo mais acessado pelos lojistas ópticos</p>
            </div>
            <Link to="/blog" className="flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-accent/80 transition-colors shrink-0">
              Ver todos os artigos <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <MaisLidos limit={isMobile ? 5 : 10} columns={isMobile ? 1 : 2} />
        </div>
      </section>
      </LazySection>

      {/* ── INSTAGRAM ────────────────────────────────────────────────── */}
      <section className="bg-[#f5f6f8] py-12">
        <div className="px-0 md:px-8">
          <div className="container flex items-center justify-between gap-4 mb-6 px-4 md:px-0">
            <div className="flex items-center gap-2">
              <Instagram className="h-5 w-5 text-accent" aria-hidden="true" />
              <h2 className="font-heading font-bold text-[#1A3870]">
                Siga{" "}
                <a
                  href="https://instagram.com/atacado_optico"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline inline-flex items-center min-h-11 py-2"
                >
                  @atacado_optico
                </a>
              </h2>
            </div>
            <a
              href="https://instagram.com/atacado_optico"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("instagram_abrir_click", { section: "home_instagram_header" })}
              className="shrink-0 flex items-center gap-1.5 text-sm font-semibold text-accent border border-accent/30 hover:border-accent px-4 py-3 min-h-11 rounded-full transition-colors hover:bg-accent/5"
              aria-label="Abrir perfil do Atacado Óptico no Instagram"
            >
              <Instagram className="h-4 w-4" aria-hidden="true" />
              Abrir Instagram
            </a>
          </div>
          <div className="max-w-[1600px] mx-auto px-0 sm:px-2">
            <BeholdInstagramFeed
              fallbackImages={instagramImages}
              onAbrirClick={() => trackEvent("instagram_abrir_click", { section: "home_instagram_error" })}
              onVerMaisClick={() => trackEvent("instagram_ver_mais_click", { section: "home_instagram_fallback" })}
            />
          </div>
          <div className="flex justify-center mt-6 px-4">
            <Button
              asChild
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <a
                href="https://instagram.com/atacado_optico"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("instagram_ver_mais_click", { section: "home_instagram_cta" })}
              >
                <Instagram className="h-5 w-5" aria-hidden="true" />
                Ver mais no Instagram
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* ── ONDE ESTAMOS ─────────────────────────────────────────────── */}
      <LazySection minHeight="320px" rootMargin="300px">
      <section className="bg-white">
        <div className="container py-10">
          <h2 className="font-heading text-lg md:text-xl font-bold text-[#1A3870] mb-6">Como nos encontrar</h2>
          <div className="grid md:grid-cols-2 gap-6 items-stretch">
            {/* Map: injected via LazySection, src loads only when near viewport */}
            <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm h-64 md:h-80">
              <iframe
                title="Localização Atacado Óptico"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-43.9416%2C-19.9218%2C-43.9357%2C-19.9178&layer=mapnik&marker=-19.9198%2C-43.9386"
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            {/* Address + CTA */}
            <div className="flex flex-col justify-center gap-4 py-2">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                </div>
                <div>
                  <p className="font-heading font-bold text-sm text-foreground">Endereço</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">Av. Afonso Pena, 726, Sala 800<br/>Centro (Praça Sete), Belo Horizonte, MG<br/>CEP 30.130-902</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.39 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.07 6.07l1.82-1.82a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                </div>
                <div>
                  <p className="font-heading font-bold text-sm text-foreground">Telefone / WhatsApp</p>
                  <a href="tel:+553125383333" className="text-sm text-muted-foreground hover:text-accent transition-colors">(31) 2538-3333</a>
                  <span className="text-muted-foreground mx-2">·</span>
                  <a href="https://wa.me/5531985015555" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-accent transition-colors">(31) 98501-5555</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2 7 12 13 22 7"/></svg>
                </div>
                <div>
                  <p className="font-heading font-bold text-sm text-foreground">E-mail</p>
                  <a href="mailto:atendimento@atacadooptico.com.br" className="text-sm text-muted-foreground hover:text-accent transition-colors">atendimento@atacadooptico.com.br</a>
                </div>
              </div>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Av.+Afonso+Pena,+726,+Centro,+Belo+Horizonte,+MG"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white font-heading font-semibold px-5 py-2.5 rounded-full text-sm transition-colors w-fit"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
                Como chegar
              </a>
            </div>
          </div>
        </div>
      </section>
      </LazySection>

      {/* ── FAQ ─────────────────────────────────────────────────────── */}
      <LazySection minHeight="300px">
        <GoogleReviews titulo="Avaliações reais de quem já comprou no Atacado Óptico" />
        <HomeFAQ />

      </LazySection>

      {/* ── NEWSLETTER ───────────────────────────────────────────────── */}
      <LazySection minHeight="160px">
      <section className="bg-[#1A3870] py-12">
        <div className="container text-center">
          <h2 className="font-heading font-bold text-white text-xl mb-2">Inscreva-se para receber Nossas Ofertas</h2>
          <p className="text-white/60 text-sm mb-6">Fique por dentro das melhores promoções e lançamentos</p>
          {newsletterDone ? (
            <p className="text-accent font-semibold text-base">Pronto! Você está inscrito.</p>
          ) : (
            <form className="flex gap-2 max-w-md mx-auto" onSubmit={handleNewsletter}>
              <label htmlFor="newsletter-email" className="sr-only">Seu melhor e-mail</label>
              <input
                id="newsletter-email"
                type="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Seu melhor e-mail"
                className="flex-1 h-11 rounded-lg px-4 text-sm bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-accent"
              />
              <Button type="submit" disabled={newsletterLoading} className="bg-accent hover:bg-accent/90 text-white font-heading font-semibold h-11 px-6">
                {newsletterLoading ? "..." : "Inscrever"}
              </Button>
            </form>
          )}
        </div>
      </section>
      </LazySection>

      <FaqSection
        items={FAQ_GERAL}
        title="Perguntas frequentes sobre óculos no atacado"
        schemaId="faq-home"
        className="bg-secondary/40"
      />

    </div>
  );
}
